import { connect } from 'react-redux';
import _compact from 'lodash/compact';
import _get from 'lodash/get';
import {
  accountSelectors,
  callSelectors,
  chatSelectors,
  conferenceParticipantsSelectors,
  conferenceSelectors,
  currentUserSelectors,
  featureSelectors,
  userProfileSelectors,
} from '@lifesize/nucleus';
import {
  closeInCallDrawer,
  openInCallDrawer,
  openAVSettingsModal,
  requestRemoteGroupUuid,
  resetRemoteGroupUuid
} from 'actions/InCall/InCallDrawer';
import Drawer from 'components/InCall/InCallDrawerHolder';
import { tooltipNotifications } from 'constants/Notification';
import {
  CALL_DETAILS_DRAWER,
  CHAT_DRAWER,
  COLLABORATION_DRAWER,
  PARTICIPANTS_DRAWER
} from 'constants/InCall/InCallDrawer';
import { ADD_PARTICIPANT_DRAWER } from 'constants/AddParticipantDrawer';
import { FLAG_IN_CALL_COLLABORATION } from 'constants/FeatureFlags';

/**
 * This container will hold all of the controls for the in call drawer.
 * Currently only tracking state and holds actions whether to open or close the
 * drawer.
 *
 * This will manage subcomponents much like FullScreenModalContainer.
 */

const mapDispatchToProps = dispatch => ({
  clearRemoteGroupUuid: () => {
    dispatch(resetRemoteGroupUuid());
  },
  onCloseClicked: () => dispatch(closeInCallDrawer()),
  onOpenClicked: (subComponentType, drawerProps) => dispatch(openInCallDrawer(subComponentType, drawerProps)),
  openAV: () => dispatch(openAVSettingsModal()),
  requestRemoteGroupUuid: (extension) => {
    dispatch(requestRemoteGroupUuid(extension));
  }
});

const mapStateToProps = (state) => {
  const activeCall = callSelectors.activeCallSelector(state);
  const extension = _get(activeCall, 'dialString');
  const remoteContact = _get(activeCall, 'remoteContact');
  const conferenceInfo = conferenceSelectors.selectConferenceInfo(state);
  const conferenceType = conferenceInfo.type;
  const chatContact = chatSelectors.gqlChatContactForCall(state);

  const unreadChats = _get(chatSelectors.getChatForWebGenerator(state)(chatContact), 'unreadMessageCount');
  const noExtension = !_get(remoteContact, 'extension');
  const isGuest = userProfileSelectors.selectIsGuest(state);
  const isLockedByModerator = conferenceSelectors.selectIsLocked(state) && !activeCall.isModerator;
  const showAddParticipantDrawer = !isGuest;
  const showCallDetailsDrawer = !isGuest || (remoteContact && !noExtension);
  // Personal (per user) Contacts and Group (per account) Contacts will have the remoteContact populated but may be cross-group
  // the extension is needed for the cross-group check (prevent the api call if the extension is not defined)
  const checkForCrossGroup = !!extension && (!remoteContact || _get(remoteContact, 'isPersonalContact') || _get(remoteContact, 'isGroupContact'));
  const localGroupUuid = currentUserSelectors.myInfo(state).groupId;
  const remoteGroupUuid = checkForCrossGroup ? _get(state, 'app.inCallDrawer.remoteGroupUuid') : localGroupUuid;
  const isCrossGroup = !isGuest && (localGroupUuid !== remoteGroupUuid);
  const isVMR = conferenceType === 'VMR'; // conference type is VRM for permanent and OTM VMRs
  const whiteboardFeatureEnabled = !!_get(accountSelectors.getAccountFeatures(state), 'whiteboarding');
  const whiteboardFlagEnabled = featureSelectors.getFeatureFlag(state)(FLAG_IN_CALL_COLLABORATION);
  const showCollaborationDrawer = whiteboardFlagEnabled && isVMR && whiteboardFeatureEnabled && !isCrossGroup;
  const showParticipantsDrawer = true;
  // const otmChatEnabled = featureSelectors.getFeatureFlag(state)(FLAG_OTM_CHAT);
  // const isOneTimeMeeting = _get(remoteContact, 'meetingType') === 'TEMP';

  /* chat enabled if:
    - Not in an escalated ADHOC call
    - chat flag is enabled
    - we're in an OTM meeting with that flag enabled
    - we're in a non passcode protected VMR
    - in any other type of call
  */
  const showChatDrawer = chatSelectors.gqlChatAvailableForCall(state);
  const raisedHandsCount = _get(conferenceParticipantsSelectors.selectParticipantsWithRaisedHands(state), 'length');
  const btnNotificationRaisedHand = _get(state, `app.notification.tooltips.${tooltipNotifications.inCallDrawerBtnRaiseHand}`) || undefined;
  const availableDrawers = _compact([
    showAddParticipantDrawer ? ADD_PARTICIPANT_DRAWER : null,
    showCallDetailsDrawer ? CALL_DETAILS_DRAWER : null,
    showParticipantsDrawer ? PARTICIPANTS_DRAWER : null,
    showChatDrawer ? CHAT_DRAWER : null,
    showCollaborationDrawer ? COLLABORATION_DRAWER : null
  ]);
  const subComponentType = _get(state, 'app.inCallDrawer.subComponentType');
  // If a drawer is selected, open it if it is available. Otherwise fall back to the first available drawer
  const visibleComponent = !!subComponentType
    ? (availableDrawers.find(drawer => drawer === subComponentType) ? subComponentType : availableDrawers[0])
    : null;

  return {
    availableDrawers,
    btnNotificationRaisedHand,
    checkForCrossGroup,
    extension,
    isGuest,
    isLockedByModerator,
    raisedHandsCount,
    unreadChats,
    visibleComponent
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Drawer);
